import { when } from 'mobx';
import { useCallback, useEffect, useMemo, useState } from 'react';

import type { Targeting } from '@feathr/blackbox';
import { TargetableClass } from '@feathr/blackbox';
import { useStore } from '@feathr/extender/state';
import type { ListResponse } from '@feathr/rachis';

import {
  getAgeDemographicTargetings,
  getGeoTargetings,
  getInterestsTargetings,
  removeOrArchiveSingleTargeting,
  removeOrArchiveTargetings,
} from '../Targetings.utils';
import type { IGeoLocationItem } from './SelectedTargetings.utils';
import { expandGeoTargetings } from './SelectedTargetings.utils';

/**
 * Props for the useSelectedTargetings hook.
 */
export interface IUseSelectedTargetingsProps {
  /** List response containing targetings */
  targetings: ListResponse<Targeting>;
}

/**
 * Return type for the useSelectedTargetings hook.
 */
interface IUseSelectedTargetingsReturn {
  /** Array of all targetings from the list */
  targetings: Targeting[];
  /** Array of geo targetings */
  geoTargetings: Targeting[];
  /** Array of age demographic targetings */
  ageDemographicTargetings: Targeting[];
  /** Array of interest targetings */
  interestsTargetings: Targeting[];
  /** Combined array of all targeting types */
  allTargetings: Targeting[];
  /** Expanded geo location items for display */
  geoLocationItems: IGeoLocationItem[];
  /** Whether geo location data is currently loading */
  isLoadingGeoLocations: boolean;
  /** Handler to remove a single targeting */
  removeTargeting: (targeting: Targeting) => void;
  /** Handler to clear all filters */
  handleClearAllFilters: () => void;
}

/**
 * Custom hook that manages state and operations for the SelectedTargetings component.
 *
 * Handles:
 * - Categorizing targetings by type (geo, age demographic, interests)
 * - Expanding geo targetings into displayable items
 * - Removing individual targetings
 * - Clearing all filters
 *
 * @param props - Configuration props
 * @returns Object containing categorized targetings and handlers
 *
 * @example
 * ```tsx
 * const {
 *   geoTargetings,
 *   interestsTargetings,
 *   removeTargeting,
 *   handleClearAllFilters,
 * } = useSelectedTargetings({ targetings: targetingsList });
 * ```
 */
export function useSelectedTargetings({
  targetings: targetingsList,
}: IUseSelectedTargetingsProps): IUseSelectedTargetingsReturn {
  const { Targetings, Targetables, GeoFilters } = useStore();

  const targetings = targetingsList.models;

  // Build a key that includes all targeting attributes that affect filtering
  // This ensures useMemo recalculates when is_archived changes (not just array length)
  const targetingKey = targetings
    .map((t) => `${t.id}:${t.get('is_archived')}:${t.get('target_data')}:${t.get('included')}`)
    .join(',');

  const geoTargetings = useMemo(
    () => getGeoTargetings(targetings),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [targetingKey],
  );
  const ageDemographicTargetings = useMemo(
    () => getAgeDemographicTargetings(targetings),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [targetingKey],
  );
  const interestsTargetings = useMemo(
    () => getInterestsTargetings(targetings),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [targetingKey],
  );

  const allTargetings = useMemo(
    () => [...geoTargetings, ...ageDemographicTargetings, ...interestsTargetings],
    [geoTargetings, ageDemographicTargetings, interestsTargetings],
  );

  const [geoLocationItems, setGeoLocationItems] = useState<IGeoLocationItem[]>([]);
  const [isLoadingGeoLocations, setIsLoadingGeoLocations] = useState(false);

  const geoFilterIdsKey = (() => {
    const geoFilterIds: string[] = [];
    for (const targeting of geoTargetings) {
      const targetableId = targeting.get('target_data');
      if (!targetableId) {
        continue;
      }

      const targetable = Targetables.get(targetableId);
      if (!targetable) {
        continue;
      }

      const targetableClass = targetable.get('_cls');

      if (targetableClass === TargetableClass.geo_set) {
        const includedGeoFilters = targetable.get('included_geo_filters') ?? [];
        const excludedGeoFilters = targetable.get('excluded_geo_filters') ?? [];
        geoFilterIds.push(...includedGeoFilters.map((id: string) => `i:${id}`));
        geoFilterIds.push(...excludedGeoFilters.map((id: string) => `e:${id}`));
      } else if (targetableClass === TargetableClass.geo) {
        const geoFilterId = targetable.get('geo_filter');
        if (geoFilterId) {
          geoFilterIds.push(geoFilterId);
        }
      }
    }
    return geoFilterIds.join(',');
  })();

  useEffect(() => {
    if (geoTargetings.length === 0) {
      setGeoLocationItems([]);
      return;
    }

    const geoFilterIds = geoFilterIdsKey
      .split(',')
      .filter(Boolean)
      .map((id) => id.replace(/^[ie]:/, ''));

    if (geoFilterIds.length === 0) {
      setGeoLocationItems([]);
      return;
    }

    setIsLoadingGeoLocations(true);

    const fetchGeoFilters = async (): Promise<void> => {
      try {
        const geoFiltersListResponse = GeoFilters.list({
          filters: {
            id__in: geoFilterIds,
          },
          pagination: {
            page: 0,
            page_size: geoFilterIds.length,
          },
        });

        await when(() => !geoFiltersListResponse.isPending);

        const items = expandGeoTargetings(geoTargetings, Targetables, GeoFilters);
        setGeoLocationItems(items);
      } catch {
        setGeoLocationItems([]);
      } finally {
        setIsLoadingGeoLocations(false);
      }
    };

    fetchGeoFilters();
  }, [geoFilterIdsKey, geoTargetings, Targetables, GeoFilters]);

  const removeTargeting = useCallback(
    (targeting: Targeting): void => {
      removeOrArchiveSingleTargeting(targeting, targetings, Targetings, { is_enabled: false });
    },
    [targetings, Targetings],
  );

  const handleClearAllFilters = useCallback((): void => {
    removeOrArchiveTargetings({
      targetingsToRemove: allTargetings,
      targetingsArray: targetings,
      Targetings,
      additionalFieldsOnArchive: { is_enabled: false },
    });
  }, [allTargetings, targetings, Targetings]);

  return {
    targetings,
    geoTargetings,
    ageDemographicTargetings,
    interestsTargetings,
    allTargetings,
    geoLocationItems,
    isLoadingGeoLocations,
    removeTargeting,
    handleClearAllFilters,
  };
}
